import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";

export default function HubPagePublic({ isDarkMode }) {
  const { hubSlug } = useParams();
  const [hub, setHub] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHub = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/hubs/${hubSlug}/`);
        if (!response.ok) {
          throw new Error("Hub not found");
        }
        const data = await response.json();
        setHub(data.hub || null);
        setVideos(data.videos || []);
      } catch (err) {
        setError(err.message || "Failed to load hub");
      } finally {
        setLoading(false);
      }
    };

    if (hubSlug) {
      fetchHub();
    }
  }, [hubSlug]);

  const pageStyle = {
    backgroundColor: isDarkMode ? "#0d0d0d" : "#f7fafd",
    minHeight: "calc(100vh - 180px)",
    padding: "20px",
  };

  const cardStyle = {
    maxWidth: "1100px",
    margin: "0 auto",
    background: isDarkMode ? "#1a1a1a" : "#fff",
    border: `1px solid ${isDarkMode ? "#2f2f2f" : "#e9edf2"}`,
    borderRadius: "12px",
    padding: "20px",
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ ...cardStyle, textAlign: "center", color: isDarkMode ? "#fff" : "#222" }}>
          Loading hub...
        </div>
      </div>
    );
  }

  if (error || !hub) {
    return (
      <div style={pageStyle}>
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <h2 style={{ color: isDarkMode ? "#fff" : "#222" }}>Hub not found</h2>
          <p style={{ color: isDarkMode ? "#b8c0cc" : "#5b6575" }}>
            This hub link is invalid or not published.
          </p>
          <Link to="/" style={{ color: "#2363eb", fontWeight: 600 }}>
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h1 style={{ marginTop: 0, color: isDarkMode ? "#fff" : "#1f2733" }}>{hub.title}</h1>
        {hub.subtitle ? (
          <p style={{ color: isDarkMode ? "#b8c0cc" : "#5b6575", marginTop: 0 }}>{hub.subtitle}</p>
        ) : null}

        {hub.intro_rich_text ? (
          <div
            style={{ color: isDarkMode ? "#d9e0eb" : "#384454", lineHeight: 1.7, marginBottom: "20px" }}
            dangerouslySetInnerHTML={{ __html: hub.intro_rich_text }}
          />
        ) : null}

        <h3 style={{ color: isDarkMode ? "#fff" : "#1f2733" }}>Videos ({videos.length})</h3>
        {videos.length === 0 ? (
          <p style={{ color: isDarkMode ? "#b8c0cc" : "#5b6575" }}>No videos are linked to this hub yet.</p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "14px" }}>
            {videos.map((video) => (
              <Link
                key={video.youtube_id}
                to={`/t/${video.youtube_id}`}
                style={{
                  textDecoration: "none",
                  background: isDarkMode ? "#111317" : "#f9fbff",
                  border: `1px solid ${isDarkMode ? "#2f2f2f" : "#e4eaf4"}`,
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                <img
                  src={video.thumbnail_url}
                  alt={video.title}
                  style={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", display: "block" }}
                />
                <div style={{ padding: "10px 12px" }}>
                  <div style={{ color: isDarkMode ? "#fff" : "#1f2733", fontWeight: 600, fontSize: "14px" }}>
                    {video.title}
                  </div>
                  <div style={{ color: isDarkMode ? "#b8c0cc" : "#5b6575", fontSize: "12px", marginTop: "4px" }}>
                    {video.channel_name || "Unknown channel"}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
